/**
 * Notification Service
 * Handles real-time notifications for meetings, invitations, and reminders
 */

import { messaging } from '../utils/firebase';
import { onMessage } from 'firebase/messaging';
import type { Notification } from '../types/user';
import { NotificationType, NotificationStatus } from '../types/user';

export interface NotificationPayload {
  type: NotificationType;
  title: string;
  message: string;
  meetingId?: string;
  meetingTitle?: string;
  inviterName?: string;
  data?: Record<string, any>;
}


const isIOS = () => {
  if (typeof navigator === "undefined") return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

/**
 * Initialize foreground message handler
 * This handles notifications when the app is in focus
 */

const getNotificationAPI = () => {
  if (typeof window === "undefined") return null;
  return "Notification" in window ? window.Notification : null;
};

export const initializeForegroundNotifications = (
  onNotificationReceived: (notification: Notification) => void
) => {
  // 🚫 iOS / SSR / Firebase messaging unavailable
  if (
    typeof window === "undefined" ||
    isIOS() ||
    !messaging
  ) {
    console.log("🚫 Foreground notifications disabled (iOS / no messaging)");
    return () => { };
  }

  let unsubscribe: (() => void) | undefined;

  (async () => {
    try {
      const { onMessage } = await import("firebase/messaging");

      const processedIds = new Set<string>();

      unsubscribe = onMessage(messaging, (payload) => {
        const notificationData = payload.data || {};

        const notificationId =
          notificationData.notificationId ||
          `notif-${notificationData.type}-${notificationData.meetingId}-${Date.now()}`;

        if (processedIds.has(notificationId)) return;
        processedIds.add(notificationId);

        const notification: Notification = {
          id: notificationId,
          type:
            (notificationData.type as NotificationType) ||
            NotificationType.SYSTEM,
          title:
            payload.notification?.title ||
            notificationData.title ||
            "Notification",
          message:
            payload.notification?.body ||
            notificationData.message ||
            "",
          status: NotificationStatus.UNREAD,
          userId: notificationData.userId || "",
          meetingId: notificationData.meetingId,
          data: { ...notificationData },
          createdAt: new Date().toISOString()
        };

        //  in-app notification
        onNotificationReceived(notification);

        //  browser notification (non-iOS only)
        const Notif = getNotificationAPI();
        if (Notif && Notif.permission === "granted") {
          showBrowserNotification(notification);
        }
      });
    } catch (err) {
      console.error(" Failed to init foreground notifications:", err);
    }
  })();

  // cleanup
  return () => {
    if (unsubscribe) unsubscribe();
  };
};


/**
 * Show browser notification
 */

export const showBrowserNotification = (notification: Notification) => {
  // 🔒 HARD SAFETY GUARD
  const Notif = getNotificationAPI();
  if (!Notif || isIOS()) return;

  try {
    const options: NotificationOptions = {
      body: notification.message,
      icon: '/Vela-Icon.png',
      tag: notification.id
    };

    new Notif(notification.title, options);
  } catch (error) {
    console.error('Error showing browser notification:', error);
  }
};


/**
 * Request notification permission
 */
export const requestNotificationPermission = async (): Promise<boolean> => {
  const Notif = getNotificationAPI();
  if (!Notif || isIOS()) {
    console.log("🚫 Notification permission not supported on iOS web");
    return false;
  }

  try {
    if (Notif.permission === "granted") return true;

    if (Notif.permission !== "denied") {
      const permission = await Notif.requestPermission();
      return permission === "granted";
    }

    return false;
  } catch (error) {
    console.error("Permission request failed:", error);
    return false;
  }
};


/**
 * Check if tab is active
 */
export const isTabActive = (): boolean => {
  return document.visibilityState === 'visible';
};

/**
 * Handle notification click
 */
export const handleNotificationClick = (
  notification: Notification,
  onNavigate: (meetingId: string) => void
) => {
  if (notification.meetingId) {
    onNavigate(notification.meetingId);
  }
};

/**
 * Handle notification action (accept/decline)
 */
export const handleNotificationAction = async (
  action: 'accept' | 'decline',
  notification: Notification,
  onAction: (action: string, notificationId: string) => void
) => {
  try {
    onAction(action, notification.id);
  } catch (error) {
    console.error('Error handling notification action:', error);
  }
};

/**
 * Schedule a meeting reminder
 */
export const scheduleMeetingReminder = (
  meetingId: string,
  meetingTitle: string,
  startTime: Date,
  reminderMinutes: number = 5
): (() => void) => {
  const now = new Date();
  const reminderTime = new Date(startTime.getTime() - reminderMinutes * 60000);
  const timeUntilReminder = reminderTime.getTime() - now.getTime();

  if (timeUntilReminder <= 0) {
    console.log('Reminder time has already passed');
    return () => { };
  }

  const timeoutId = setTimeout(() => {
    const notification: Notification = {
      id: `reminder-${meetingId}-${Date.now()}`,
      type: NotificationType.MEETING_REMINDER,
      title: 'Meeting Starting Soon',
      message: `${meetingTitle} starts in ${reminderMinutes} minutes`,
      status: NotificationStatus.UNREAD,
      userId: '',
      meetingId,
      data: {
        meetingTitle,
        reminderMinutes,
        startTime: startTime.toISOString()
      },
      createdAt: new Date().toISOString()
    };

    if (!isIOS() && isTabActive()) {
      showBrowserNotification(notification);
    }

  }, timeUntilReminder);

  // Return cleanup function
  return () => clearTimeout(timeoutId);
};

/**
 * Format notification time
 */
export const formatNotificationTime = (createdAt: string): string => {
  const now = new Date();
  const created = new Date(createdAt);
  const diffInMinutes = Math.floor((now.getTime() - created.getTime()) / 60000);

  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
  if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
  return `${Math.floor(diffInMinutes / 1440)}d ago`;
};
